import React, { useState, useEffect, useCallback } from "react";
import { FaEnvelopeOpenText, FaPaperPlane, FaCheck } from "react-icons/fa";
import { useAlert } from "../../../context/AlertContext";
import { Drawer, MascotLoadingOverlay, ReportPreviewModal } from "../../shared";
import { TextInput, NumberInput } from "../../atoms";
import { REPORT_SECTIONS, DEFAULT_SECTIONS_ALL_ON, anySectionOn } from "./reportSections";
import {
  FREQUENCIES,
  COVERAGE_OPTIONS,
  WEEK_DAYS,
  normalizeReportConfig,
  scheduleSummary,
  sectionsLabel,
  reportConfigError,
} from "./autoReportConfig";

/**
 * Otomatik rapor e-postası ayar çekmecesi — firma ve kullanıcı için ortak.
 * Veriyi çağıran sarmalayıcı getirir/kaydeder; bu bileşen formu, doğrulamayı
 * ve önizle-gönder akışını yönetir.
 *
 * status: "loading" | "error" | "unavailable" | "ready"
 * onSave(config), onPreview(freq, cfg) → { pdfBase64, reportType, dateRange },
 * onSend(freq, cfg) — hepsi promise; hata fırlatırsa mesajı toast'a düşer.
 */

const EMPTY_PREVIEW = { open: false, frequency: null, pdfBase64: null, reportType: null, dateRange: null };

const FieldLabel = ({ children }) => (
  <span className="block text-xs font-SemiBold text-gray-600 mb-1.5">{children}</span>
);

const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-sixth" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      }`}
    />
  </button>
);

const Chip = ({ active, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`px-3 py-1.5 text-xs font-Medium rounded-lg border transition-colors ${
      active ? "bg-main text-white border-main" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
    } ${className}`}
  >
    {children}
  </button>
);

function FrequencyCard({ freqKey, label, Icon, cfg, onChange, onPreview, previewDisabled, isPreviewingThis }) {
  const sectionsOk = anySectionOn(cfg.sections);
  const daysOk = freqKey !== "weekly" || cfg.daysOfWeek?.length > 0;

  const toggle = () => {
    // Haftalık açılırken gün yoksa Pazartesi ile başlar — boş günle
    // kaydedilemez, kullanıcı ilk açılışta hata görmesin.
    const enabling = !cfg.enabled;
    const needsDay = enabling && freqKey === "weekly" && !(cfg.daysOfWeek?.length > 0);
    onChange(needsDay ? { enabled: true, daysOfWeek: ["mon"] } : { enabled: enabling });
  };

  return (
    <section
      className={`rounded-xl border bg-white transition-colors ${
        cfg.enabled ? "border-sixth/30 shadow-sm" : "border-gray-200"
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            cfg.enabled ? "bg-sixth/10 text-sixth" : "bg-gray-100 text-gray-400"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-SemiBold text-gray-800">{label} Rapor</p>
          <p className="text-xs text-gray-500 truncate">
            {cfg.enabled ? scheduleSummary(freqKey, cfg) : "Kapalı"}
          </p>
        </div>
        <Switch checked={!!cfg.enabled} label={`${label} rapor aç/kapat`} onChange={toggle} />
      </div>

      {cfg.enabled && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-4">
          <div className={`grid gap-3 ${freqKey === "monthly" ? "grid-cols-2" : "grid-cols-1"}`}>
            <div>
              <FieldLabel>Gönderim saati</FieldLabel>
              <TextInput
                type="time"
                className="!py-2 !rounded-xl"
                value={cfg.sendTime || "08:00"}
                onChange={(e) => onChange({ sendTime: e.target.value || "08:00" })}
              />
            </div>
            {freqKey === "monthly" && (
              <div>
                <FieldLabel>Ayın günü</FieldLabel>
                <NumberInput
                  min={1}
                  max={31}
                  className="!py-2 !rounded-xl"
                  value={cfg.dayOfMonth ?? 1}
                  onChange={(v) => onChange({ dayOfMonth: Math.min(Math.max(Number(v) || 1, 1), 31) })}
                />
              </div>
            )}
          </div>

          {freqKey === "weekly" && (
            <div>
              <FieldLabel>Gönderim günleri</FieldLabel>
              <div className="grid grid-cols-7 gap-1">
                {WEEK_DAYS.map((day) => {
                  const active = !!cfg.daysOfWeek?.includes(day.id);
                  return (
                    <Chip
                      key={day.id}
                      active={active}
                      className="!px-0"
                      onClick={() => {
                        const days = cfg.daysOfWeek || [];
                        onChange({ daysOfWeek: active ? days.filter((d) => d !== day.id) : [...days, day.id] });
                      }}
                    >
                      {day.label}
                    </Chip>
                  );
                })}
              </div>
              {!daysOk && <p className="text-xs text-fifth mt-1.5">En az 1 gün seçmelisiniz.</p>}
            </div>
          )}

          <div>
            <FieldLabel>Rapor dönemi</FieldLabel>
            <div className="grid grid-cols-3 gap-1.5">
              {COVERAGE_OPTIONS[freqKey].map((opt) => (
                <Chip
                  key={opt.value}
                  active={cfg.coverage === opt.value}
                  className="!px-1"
                  onClick={() => onChange({ coverage: opt.value })}
                >
                  {opt.label}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <FieldLabel>Rapor bölümleri · {sectionsLabel(cfg)}</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              {REPORT_SECTIONS.map(({ key: sKey, label: sLabel, Icon: SIcon }) => {
                const active = !!cfg.sections?.[sKey];
                return (
                  <button
                    key={sKey}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      onChange({ sections: { ...(cfg.sections || DEFAULT_SECTIONS_ALL_ON), [sKey]: !active } })
                    }
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-Medium transition-colors ${
                      active ? "bg-main/5 text-main border-main/30" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <SIcon className={`w-3.5 h-3.5 shrink-0 ${active ? "text-main" : "text-gray-400"}`} />
                    <span className="flex-1 text-left truncate">{sLabel}</span>
                    <span
                      className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 ${
                        active ? "bg-main border-main text-white" : "border-gray-300"
                      }`}
                    >
                      {active && <FaCheck className="w-2.5 h-2.5" />}
                    </span>
                  </button>
                );
              })}
            </div>
            {!sectionsOk && <p className="text-xs text-fifth mt-1.5">En az 1 rapor bölümü seçmelisiniz.</p>}
          </div>

          <button
            type="button"
            onClick={onPreview}
            disabled={previewDisabled || !sectionsOk}
            className="w-full inline-flex items-center justify-center gap-2 py-2 text-xs font-SemiBold rounded-lg border border-main text-main bg-white hover:bg-main hover:text-white transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
          >
            <FaPaperPlane className="w-3 h-3" />
            {isPreviewingThis ? "Önizleme hazırlanıyor..." : "Şimdi Gönder (Önizle ve Gönder)"}
          </button>
        </div>
      )}
    </section>
  );
}

export default function AutoReportDrawer({
  open,
  onClose,
  title,
  subtitle,
  status,
  message,
  recipient,
  savedConfig,
  onSave,
  onPreview,
  onSend,
  isSaving = false,
  isPreviewing = false,
  isSending = false,
}) {
  const { showSuccess, showError } = useAlert();
  const [config, setConfig] = useState(() => normalizeReportConfig(savedConfig));
  const [preview, setPreview] = useState(EMPTY_PREVIEW);

  // Her açılışta kayıtlı ayardan başlar — iptal edilen düzenleme bir
  // sonraki açılışa taşınmasın.
  useEffect(() => {
    if (open) setConfig(normalizeReportConfig(savedConfig));
  }, [open, savedConfig]);

  const update = useCallback((key, updates) => {
    setConfig((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  }, []);

  const validationError = reportConfigError(config);
  const ready = status === "ready";

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSave = async () => {
    if (validationError) {
      showError(validationError);
      return;
    }
    try {
      await onSave(config);
      showSuccess("Ayarlar kaydedildi");
      onClose();
    } catch (err) {
      showError(err?.data?.message || "Kayıt başarısız");
    }
  };

  const handlePreview = async (frequency) => {
    try {
      const data = await onPreview(frequency, config[frequency]);
      setPreview({
        open: true,
        frequency,
        pdfBase64: data?.pdfBase64 || null,
        reportType: data?.reportType || null,
        dateRange: data?.dateRange || null,
      });
    } catch (err) {
      showError(err?.data?.message || "Rapor önizleme alınamadı");
    }
  };

  const handleConfirmSend = async () => {
    if (!preview.frequency) return;
    try {
      await onSend(preview.frequency, config[preview.frequency]);
      showSuccess("Rapor e-posta ile gönderildi");
      setPreview(EMPTY_PREVIEW);
    } catch (err) {
      showError(err?.data?.message || "Rapor gönderilemedi");
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        width="md"
        title={title}
        subtitle={subtitle}
        titleIcon={<FaEnvelopeOpenText className="w-4 h-4 text-slate-600" />}
      >
        <Drawer.Body className="p-5 space-y-4 bg-gray-50/60">
          {status === "loading" ? (
            <div className="relative space-y-3">
              <MascotLoadingOverlay message="Rapor ayarları yükleniyor..." />
              {FREQUENCIES.map(({ key }) => (
                <div key={key} className="h-[62px] rounded-xl border border-gray-200 bg-white animate-pulse" />
              ))}
            </div>
          ) : !ready ? (
            <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600">{message}</p>
          ) : (
            <>
              {recipient && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-xs text-gray-600 leading-relaxed">
                  {recipient}
                </div>
              )}
              {FREQUENCIES.map(({ key, label, Icon }) => (
                <FrequencyCard
                  key={key}
                  freqKey={key}
                  label={label}
                  Icon={Icon}
                  cfg={config[key]}
                  onChange={(updates) => update(key, updates)}
                  onPreview={() => handlePreview(key)}
                  previewDisabled={isPreviewing || isSending}
                  isPreviewingThis={isPreviewing && preview.frequency === key}
                />
              ))}
            </>
          )}
        </Drawer.Body>

        {ready && (
          <Drawer.Footer className="px-5 py-4 border-t border-gray-200 bg-white">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !!validationError}
                className="px-5 py-2 text-sm font-SemiBold text-white bg-main rounded-lg hover:bg-mainSecond transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </Drawer.Footer>
        )}
      </Drawer>

      <ReportPreviewModal
        open={preview.open}
        onClose={() => !isSending && setPreview(EMPTY_PREVIEW)}
        pdfBase64={preview.pdfBase64}
        frequency={preview.frequency}
        reportType={preview.reportType}
        dateRange={preview.dateRange}
        onConfirm={handleConfirmSend}
        isSending={isSending}
      />
    </>
  );
}
